import { useEffect, useState } from "react";

export type RecentSearchKind = "policy" | "agency" | "claim";

export interface RecentSearch {
  kind: RecentSearchKind;
  number: string;
  name: string | null;
  ts: number;
}

const STORAGE_KEY = "memic-recent-searches";
const MAX_ITEMS = 10;

// Window event lets multiple Home-page mounts (and other tabs in the same
// window) stay in sync without a full re-mount. Cross-tab changes also fire
// `storage` natively; both handlers refresh state from the source of truth.
const CHANGE_EVENT = "memic-recent-searches-change";

function readStorage(): RecentSearch[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const out: RecentSearch[] = [];
    for (const item of parsed) {
      if (
        item &&
        typeof item === "object" &&
        "kind" in item &&
        "number" in item &&
        "ts" in item &&
        (item.kind === "policy" || item.kind === "agency" || item.kind === "claim") &&
        typeof item.number === "string" &&
        typeof item.ts === "number"
      ) {
        const name =
          "name" in item && (typeof item.name === "string" || item.name === null)
            ? item.name
            : null;
        out.push({ kind: item.kind, number: item.number, name, ts: item.ts });
      }
    }
    return out;
  } catch {
    return [];
  }
}

function writeStorage(items: RecentSearch[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch {
    // Quota / disabled storage — silently drop. Recent searches are a UX nicety.
  }
}

export function addRecentSearch(entry: Omit<RecentSearch, "ts">): void {
  const existing = readStorage();
  const filtered = existing.filter(
    (item) => !(item.kind === entry.kind && item.number === entry.number),
  );
  const next = [{ ...entry, ts: Date.now() }, ...filtered].slice(0, MAX_ITEMS);
  writeStorage(next);
}

export function clearRecentSearches(): void {
  writeStorage([]);
}

export function useRecentSearches(): RecentSearch[] {
  const [items, setItems] = useState<RecentSearch[]>(() => readStorage());

  useEffect(() => {
    const refresh = () => setItems(readStorage());
    window.addEventListener(CHANGE_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  return items;
}
